import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import chatService from './chatService';
import { errorHandlers } from './serviceUtils';
import { Agent, Conversation, ConversationResponse, ConversationMessageResponse, Message, User, ConversationResponseData, UserStatus, UserRole, ConversationAgentResponseData, SendMessagePayload, SendAgentMessagePayload, ActiveConversationResponse, ActiveConversationResponseData } from '../chatTypes';
import { getAgentId, getAgentUniqId, getUserName } from '../chatUtils';
import { getUserId } from '../chatUtils';
import { websocketService } from '.';
import { WebSocketState } from './websocketService';
import chatProperties from '../config/chatProperties';

export const useSearch = (isOpen: boolean, isSearchOpen: boolean) => {
    const [users, setUsers] = useState<User[]>([]);
    const [searchLoading, setSearchLoading] = useState(false);
    const [searchError, setSearchError] = useState<string | null>(null);

    useEffect(() => {
        const controller = new AbortController();
        if (isSearchOpen) {
            search(controller.signal);
        }
        return () => {
            controller.abort();
        }
    }, [isSearchOpen]);

    const search = useCallback(async (signal?: AbortSignal) => {
        setSearchLoading(true);
        setSearchError(null);
        try {
            const controller = signal ? undefined : new AbortController();
            const searchSignal = signal || (controller?.signal);
            const data = await chatService.search.query(searchSignal);
            if (chatProperties.type === 'Agent') {
                if (data && typeof data === 'object' && 'type' in data && 'userList' in data && Array.isArray(data?.userList) && data?.userList?.length > 0) {
                    const usersParsed = data.userList.map((user: any) => ({
                        id: user.subUserId,
                        name: user.subUserName,
                        role: 'admin' as UserRole,
                        status: 'online' as UserStatus,
                        parentUserId: user.parentUserId,
                    }));
                    setUsers(usersParsed);
                } else {
                    throw new Error("Invalid response from server");
                }
            } else {
                if (data && data?.Status && data?.Status === "Success") {
                    if (data?.Data) {
                        const parsedData: [string, Agent[]] = JSON.parse(data?.Data);
                        const uniqueAgents = new Map<string, User>();
                        for (const agentList in parsedData) {
                            const agentListArray = parsedData[agentList];
                            if (Array.isArray(agentListArray)) {
                                agentListArray.forEach((agent: Agent) => {
                                    if (agent && typeof agent === 'object' && 'AgentId' in agent && 'AgentName' in agent) {
                                        uniqueAgents.set(agent.AgentId.toString(), {
                                            id: agent.AgentId,
                                            name: agent.Agent_Id,
                                            role: 'agent',
                                            status: 'online'
                                        })
                                    }
                                });
                            }
                        }
                        const resultsArray = Array.from(uniqueAgents.values());
                        setUsers(resultsArray);
                    }
                }
            }
            return data;
        } catch (err: unknown) {
            setSearchError(errorHandlers.getErrorMessage(err));
            return [];
        } finally {
            setSearchLoading(false);
        }
    }, []);

    return {
        users,
        setUsers,
        searchLoading,
        searchError,
        search
    };
};

export const useConversation = (isOpen: boolean, isSearchOpen: boolean) => {
    const [conversations, setConversations] = useState<Conversation[]>([]);
    // const [activeConversation, setActiveConversation] = useState<Conversation | null>(null);
    const [activeConversationLoading, setActiveConversationLoading] = useState(false);
    const [activeConversationError, setActiveConversationError] = useState<string | null>(null);
    const [sendMessageError, setSendMessageError] = useState<string | null>(null);
    const [sendMessageLoading, setSendMessageLoading] = useState(false);
    const [activeConversationId, setActiveConversationId] = useState<number | null>(null);
    const activeConversation = useMemo(() => {
        if (conversations && Array.isArray(conversations) && conversations.length > 0) {
            return conversations.find((c) => c.id === activeConversationId) || null;
        }
        return null;
    }, [conversations, activeConversationId]);
    const { users, searchLoading } = useSearch(isOpen, isSearchOpen);

    useEffect(() => {
        const controller = new AbortController();
        getActiveConversations(controller.signal);
        return () => {
            controller.abort();
        }
    }, []);

    useEffect(() => {
        if (isOpen) {
            handleUnreadCountOnOpen()
        }
    }, [isOpen]);

    useEffect(() => {
        const controller = new AbortController();
        if (activeConversation) {
            getConversation({
                ...activeConversation,
                unreadCount: isOpen ? 0 : activeConversation?.unreadCount ? activeConversation?.unreadCount + 1 : 1
            }, controller.signal, 'activeConversationChange');
        }
        return () => {
            controller.abort();
        }
    }, [activeConversationId]);

    console.log({
        users,
        conversations,
        activeConversation,
        activeConversationId,
    });

    async function getActiveConversations(signal?: AbortSignal) {
        try {
            const response: ConversationResponse = await chatService.conversations.getAll(signal || new AbortController().signal);
            if (response && typeof response === 'object' && 'Status' in response && response.Status === "Success") {
                let parsedResponse: ActiveConversationResponseData = JSON.parse(response?.Data);
                if (parsedResponse && typeof parsedResponse === 'object' && 'data' in parsedResponse && Array.isArray(parsedResponse.data) && parsedResponse.data.length > 0) {
                    setConversations((prev: Conversation[] | null | undefined) => {
                        const isPrev = prev && Array.isArray(prev) && prev.length > 0;
                        const prevConversations = isPrev ? [...prev] : [];
                        if (prevConversations && Array.isArray(prevConversations)) {
                            parsedResponse.data.forEach((conversationFromApi: ActiveConversationResponse) => {
                                const isExistingConversation = prevConversations.find((c: Conversation) => c.id === parseInt(conversationFromApi.id));
                                if (!isExistingConversation) {
                                    prevConversations.push({
                                        id: parseInt(conversationFromApi.id),
                                        title: conversationFromApi.name,
                                        participants: [{
                                            id: parseInt(conversationFromApi.id),
                                            name: conversationFromApi.name,
                                            role: 'agent',
                                            status: 'online',
                                        }],
                                        messages: [],
                                        unreadCount: 0,
                                        isGroup: false,
                                        createdAt: new Date(),
                                    });
                                }
                            });
                        }
                        return prevConversations;
                    });
                }
            }
        } catch (err: unknown) {
            console.error(err);
        }
    }

    function handleUnreadCountOnOpen() {
        if (activeConversationId) {
            setConversations((prev: Conversation[] | null | undefined) => {
                const isPrev = prev && Array.isArray(prev) && prev.length > 0;
                const isExistingConversation = isPrev ? prev.find((c: Conversation) => c.id === activeConversationId) : null;
                if (isPrev && isExistingConversation) {
                    return prev.map((c: Conversation) => {
                        if (c.id === activeConversationId) {
                            return {
                                ...c,
                                unreadCount: 0,
                            };
                        }
                        return c;
                    });
                }
                return prev || [];
            });
        }
    }

    function getParticipants(senderId: string) {
        console.log("getParticipants", senderId, users, Array.isArray(users), users.length > 0);
        if (users && Array.isArray(users) && users.length > 0) {
            return users.filter((user: User) => user.id === parseInt(senderId));
        }
        return [];
    }


    const getConversation = useCallback(async (conversation: Conversation, signal?: AbortSignal, type?: string) => {
        console.log("getConversation", conversation);
        setActiveConversationLoading(true);
        setActiveConversationError(null);
        try {
            const conversationId = conversation.id;
            let response: ConversationResponse | ConversationAgentResponseData = await chatService.conversations.getById(conversationId, signal || new AbortController().signal, conversation?.title || '');
            let messages: Message[] = [];
            if (chatProperties.type === 'Agent') {
                response = response as unknown as ConversationAgentResponseData;
                if (response && typeof response === 'object' && 'status' in response && 'hist' in response && response.status === "success" && Array.isArray(response.hist) && response.hist.length > 0) {
                    messages = response.hist.map((message: ConversationMessageResponse, index: number) => ({
                        id: `msg-${conversationId}-${index}-${new Date(new Date(message.MessageSentTime)).getTime()}`,
                        senderId: message.MessageInitiator !== 'Agent' ? getAgentUniqId() : conversation.id.toString(),
                        senderName: message.MessageInitiator !== 'Agent' ? getAgentId() : conversation.title,
                        content: message.Message,
                        timestamp: new Date(new Date(message.MessageSentTime)),
                        isRead: true,
                        isSent: true,
                    }));
                }
            } else if (response && typeof response === 'object' && 'Status' in response && 'Data' in response && response.Status === "Success") {
                const parsedResponse: ConversationResponseData = JSON.parse(response?.Data);
                messages = parsedResponse.data.map((message: ConversationMessageResponse, index: number) => ({
                    id: `msg-${conversationId}-${index}-${new Date(new Date(message.MessageSentTime)).getTime()}`,
                    senderId: message.MessageInitiator === 'Agent' ? conversation.id.toString() : getUserId(),
                    senderName: message.MessageInitiator === 'Agent' ? conversation.title : getUserName(),
                    content: message.Message,
                    timestamp: new Date(new Date(message.MessageSentTime)),
                    isRead: true,
                    isSent: true,
                }));
            }
            messages.sort((a: Message, b: Message) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());
            setConversations((prev: Conversation[] | null | undefined) => {
                const isPrev = prev && Array.isArray(prev) && prev.length > 0;
                const isExistingConversation = isPrev ? prev.find((c: Conversation) => c.id === conversationId) : null;
                if (isPrev && isExistingConversation) {
                    try {
                        return prev.map((c: Conversation) => {
                            if (c.id === conversationId) {
                                const prevConversation = c;
                                return {
                                    ...prevConversation,
                                    id: conversationId,
                                    title: conversation?.title || prevConversation?.title || conversationId.toString(),
                                    participants: conversation.participants || prevConversation?.participants || [],
                                    messages: messages,
                                    lastMessage: messages[messages.length - 1],
                                    unreadCount: conversation?.unreadCount || 0,
                                    isGroup: false,
                                    createdAt: conversation?.createdAt || prevConversation?.createdAt || new Date(),
                                };
                            }
                            return c;
                        })
                    } catch (err: unknown) {
                        console.error(err);
                        return prev;
                    }
                }
                if (!isExistingConversation && !activeConversationId) {
                    let participants = getParticipants(conversationId.toString());
                    if (participants && Array.isArray(participants) && participants.length === 0) {
                        const participant: User = {
                            id: parseInt(conversationId.toString()),
                            name: conversation?.title || '',
                            role: 'agent',
                            status: 'online',
                        }
                        participants = [participant];
                    }
                    if (participants && Array.isArray(participants) && participants.length > 0) {
                        const newConversation: Conversation = {
                            id: conversationId,
                            title: conversation?.title || '',
                            messages: messages,
                            participants: participants,
                            createdAt: new Date(),
                            unreadCount: type === 'websocket' ? 1 : 0,
                            isGroup: false,
                        }
                        if (prev?.length && Array.isArray(prev) && prev.length > 0) {
                            return [...prev, newConversation];
                        } else {
                            return [newConversation];
                        }
                    }
                }
                return prev || [];
            });
            if (!activeConversationId && type !== 'websocket') {
                setActiveConversationId(conversationId);
            }
        } catch (err: unknown) {
            console.error(err);
            setActiveConversationError(errorHandlers.getErrorMessage(err));
        } finally {
            setActiveConversationLoading(false);
        }
    }, [users]);

    const sendMessage = useCallback(async (message: Message, conversation: Conversation) => {
        if (!activeConversation) return;
        try {
            setSendMessageLoading(true);
            const adminSendMessagePayload: SendMessagePayload = {
                agentUniqId: activeConversation?.id || 0,
                agentId: activeConversation?.title || '',
                subUserId: getUserId(),
                subUserName: getUserName(),
                msg: message.content,
            }
            const agentSendMessagePayload: SendAgentMessagePayload = {
                subUserId: activeConversation?.id || 0,
                customer: activeConversation?.title || '',
                subUserName: activeConversation?.title || '',
                parentUserId: activeConversation?.parentUserId || 0,
                msg: message.content,
            }
            const response: { Status?: string, Message?: string, type?: string, status?: string } = await chatService.messages.send(chatProperties.type === 'Agent' ? agentSendMessagePayload : adminSendMessagePayload);
            if (chatProperties.type === 'Agent') {
                if (response && typeof response === 'object' && 'type' in response && response.status === "success") {
                    console.log("sent message", response);
                } else {
                    throw new Error(response?.Message || 'Failed to send message');
                }
            } else {
                if (response && typeof response === 'object' && 'Status' in response && response.Status === "Success") {
                    console.log("sent message", response);
                } else {
                    throw new Error(response?.Message || 'Failed to send message');
                }
            }
        } catch (err: unknown) {
            const updatedConversation = { ...conversation }
            updatedConversation.messages = updatedConversation.messages.filter((m) => m.id !== message.id)
            setActiveConversationId(updatedConversation.id);
            console.error(err);
            setSendMessageError(errorHandlers.getErrorMessage(err));
        } finally {
            setSendMessageLoading(false);
        }
    }, [activeConversation]);

    return {
        activeConversation,
        activeConversationId,
        activeConversationLoading,
        activeConversationError,
        setActiveConversationId,
        getConversation,
        sendMessage,
        conversations,
        setConversations,
        sendMessageError,
        sendMessageLoading,
        getParticipants,
        users,
        searchLoading,
    }
}


export const useWebSocket = (userId: string, autoConnect: boolean = true) => {
    const [connectionState, setConnectionState] = useState<WebSocketState>(WebSocketState.CLOSED);
    const [isConnected, setIsConnected] = useState(false);
    const [error, setError] = useState<Error | null>(null);
    const [newMessageFromWebSocket, setNewMessageFromWebSocket] = useState<Message | null>(null);

    // Use refs to maintain latest state in callbacks without dependencies
    const connectedRef = useRef(isConnected);
    connectedRef.current = isConnected;

    // Connect to WebSocket
    const connect = useCallback(async () => {
        if (!userId) return;

        try {
            setError(null);
            await websocketService.connect(userId);
            setConnectionState(websocketService.getState());
        } catch (err) {
            setError(err as Error);
        }
    }, [userId]);

    // Disconnect from WebSocket
    const disconnect = useCallback(() => {
        websocketService.disconnect();
        setConnectionState(WebSocketState.CLOSED);
    }, []);

    // Subscribe to events when component mounts
    useEffect(() => {
        console.log('useEffect for websocket', userId, autoConnect);
        if (!userId) return;

        // Auto-connect if requested
        if (autoConnect) {
            connect();
        }

        // Set up event handlers
        const connectionHandler = (status: boolean) => {
            setIsConnected(status);
            setConnectionState(websocketService.getState());
        };

        const errorHandler = (err: Error) => {
            setError(err);
        };

        const messageHandler = (message: Message) => {
            setNewMessageFromWebSocket(message);
        };

        // Subscribe to events
        websocketService.onConnectionChange(connectionHandler);
        const unsubErrorHandler = websocketService.onError(errorHandler);
        const unsubMessageHandler = websocketService.onNewMessage(messageHandler);

        // Cleanup subscriptions
        return () => {
            unsubErrorHandler();
            unsubMessageHandler();

            // Disconnect if auto-connected
            if (autoConnect) {
                disconnect();
            }
            console.log('unsubscribed from websocket', userId, autoConnect);
        };
    }, [userId, autoConnect]);


    return {
        connectionState,
        isConnected,
        error,
        connect,
        disconnect,
        newMessageFromWebSocket,
    };
}; 